import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  useStaffFacilities,
  useStaffParticipants,
  useValidateChatParticipant,
  useAddChatroomParticipant,
  useChatroomParticipants,
  chatKeys,
} from "../../features/chat";
import type { StaffParticipant } from "../../features/chat/types";
import { useBoundStore } from "../../store";

interface AddParticipantLocationState {
  chatroomId: string;
  externalSiteId: string | null;
}

export interface AddParticipantViewModelState {
  facilityName: string;
  facilitiesLoading: boolean;
  userSearch: string;
  selectedSsoIds: string[];
  participants: StaffParticipant[];
  participantsLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextParticipants: boolean;
  isAdding: boolean;
  addError: string | null;
  isValidating: boolean;
  rejectedDialogVisible: boolean;
  rejectedParticipants: StaffParticipant[];
  canProceedWithOthers: boolean;
  canAdd: boolean;
  existingExternalIds: Set<string>;
  onUserSearchChange: (query: string) => void;
  onToggleSsoId: (ssoId: string) => void;
  onLoadMoreParticipants: () => void;
  onAdd: () => void;
  onProceedWithOthers: () => void;
  onCancelRejected: () => void;
  onCancel: () => void;
}

export function useAddParticipantViewModel(): AddParticipantViewModelState {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { chatroomId, externalSiteId } = (location.state ?? {}) as AddParticipantLocationState;

  const [userSearch, setUserSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedSsoIds, setSelectedSsoIds] = useState<string[]>([]);
  const [rejectedSsoIds, setRejectedSsoIds] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const handleCancel = useCallback(() => navigate(-1), [navigate]);

  const { mutateAsync: validateParticipants } = useValidateChatParticipant();
  const { mutateAsync: addChatroomParticipant } = useAddChatroomParticipant();

  const { data: facilitiesData, isLoading: facilitiesLoading } = useStaffFacilities();
  const { data: existingParticipantsData = [] } = useChatroomParticipants(chatroomId, !!chatroomId);

  const existingExternalIds = useMemo(
    () => new Set(existingParticipantsData.map((p) => p.externalUserId).filter((id): id is string => !!id)),
    [existingParticipantsData],
  );

  const facilityName = useMemo(() => {
    if (!facilitiesData || !externalSiteId) return "";
    return facilitiesData.find((f) => f.facilityGlobalId === externalSiteId)?.facilityName ?? "";
  }, [facilitiesData, externalSiteId]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(userSearch), 400);
    return () => clearTimeout(timer);
  }, [userSearch]);

  const {
    data: participantsPages,
    isLoading: participantsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useStaffParticipants(
    { facilityGuid: externalSiteId ?? "", q: debouncedSearch || undefined },
    !!externalSiteId,
  );

  const participants = useMemo(
    () => (participantsPages?.pages ?? []).flatMap((p) => p?.data ?? []),
    [participantsPages],
  );

  const handleToggleSsoId = useCallback(
    (ssoId: string) => {
      if (existingExternalIds.has(ssoId)) return;
      setSelectedSsoIds((prev) =>
        prev.includes(ssoId) ? prev.filter((x) => x !== ssoId) : [...prev, ssoId],
      );
    },
    [existingExternalIds],
  );

  const doAdd = useCallback(
    (ssoIds: string[]) => {
      if (!externalSiteId || !chatroomId || ssoIds.length === 0) return;
      setIsAdding(true);
      setAddError(null);
      addChatroomParticipant({
        chatRoomId: Number(chatroomId),
        externalUserIds: ssoIds,
        associateUserExternalId: "",
        responseWithError: true,
      })
        .then(() => {
          void queryClient.invalidateQueries({ queryKey: chatKeys.participants(chatroomId) });
          useBoundStore.getState().setPendingNewChatroomId(chatroomId);
          navigate(-1);
        })
        .catch(() => setAddError("Failed to add participant(s). Please try again."))
        .finally(() => setIsAdding(false));
    },
    [chatroomId, externalSiteId, addChatroomParticipant, queryClient, navigate],
  );

  const handleAdd = useCallback(() => {
    if (selectedSsoIds.length === 0) return;
    setIsValidating(true);
    validateParticipants({ externalUserIds: selectedSsoIds })
      .then((result) => {
        if (result.rejectedUsers?.length) {
          setRejectedSsoIds(result.rejectedUsers);
        } else {
          doAdd(selectedSsoIds);
        }
      })
      .catch(() => doAdd(selectedSsoIds))
      .finally(() => setIsValidating(false));
  }, [selectedSsoIds, validateParticipants, doAdd]);

  const onLoadMoreParticipants = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const rejectedParticipants = useMemo(
    () =>
      rejectedSsoIds.map(
        (ssoId) =>
          participants.find((p) => p.ssoId === ssoId) ?? { ssoId, displayName: ssoId, role: "" },
      ),
    [rejectedSsoIds, participants],
  );

  const onProceedWithOthers = useCallback(() => {
    const validIds = selectedSsoIds.filter((id) => !rejectedSsoIds.includes(id));
    setSelectedSsoIds(validIds);
    setRejectedSsoIds([]);
    doAdd(validIds);
  }, [selectedSsoIds, rejectedSsoIds, doAdd]);

  return {
    facilityName,
    facilitiesLoading,
    userSearch,
    selectedSsoIds,
    participants,
    participantsLoading,
    isFetchingNextPage,
    hasNextParticipants: !!hasNextPage,
    isAdding,
    addError,
    isValidating,
    rejectedDialogVisible: rejectedSsoIds.length > 0,
    rejectedParticipants,
    canProceedWithOthers: selectedSsoIds.length > rejectedSsoIds.length,
    canAdd: selectedSsoIds.length > 0,
    existingExternalIds,
    onUserSearchChange: setUserSearch,
    onToggleSsoId: handleToggleSsoId,
    onLoadMoreParticipants,
    onAdd: handleAdd,
    onProceedWithOthers,
    onCancelRejected: () => setRejectedSsoIds([]),
    onCancel: handleCancel,
  };
}
